/* NotificationBell — premium trigger + full-screen sheet.
   Slim orchestrator; card + sheet + helpers live under ./notificationBell/*. */
import { useCallback, useEffect, useState } from "react";
import { Bell, BellRinging } from "@phosphor-icons/react";
import {
  deleteNotification, getUnreadCount, listNotifications, markAllRead, markRead,
} from "../../lib/notificationsApi";

import NotificationSheet from "./notificationBell/NotificationSheet";
import { POLL_MS } from "./notificationBell/_shared";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listNotifications({ limit: 80 });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch { /* silent — network hiccup shouldn't blow up the header */ }
    finally { setLoading(false); }
  }, []);

  const pollUnread = useCallback(async () => {
    try { setUnread(await getUnreadCount()); } catch { /* ignore */ }
  }, []);

  // Visibility-aware polling: pause the 30-s interval when the tab is
  // backgrounded, then catch-up + resume on return. Cuts ~120 idle
  // requests/hour per signed-in user.
  useEffect(() => {
    pollUnread();
    let t = null;
    const start = () => { if (t == null) t = setInterval(pollUnread, POLL_MS); };
    const stop = () => { if (t != null) { clearInterval(t); t = null; } };
    const onVis = () => {
      if (document.hidden) stop();
      else { pollUnread(); start(); }
    };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [pollUnread]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.dispatchEvent(new CustomEvent("lexiq:notif-open"));
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onNavTap = () => setOpen(false);
    window.addEventListener("keydown", onKey);
    window.addEventListener("lexiq:nav-tap", onNavTap);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("lexiq:nav-tap", onNavTap);
      window.dispatchEvent(new CustomEvent("lexiq:notif-close"));
    };
  }, [open]);

  const handleRead = async (id) => {
    setItems((p) => p.map((x) => (x.id === id ? { ...x, is_read: true } : x)));
    setUnread((u) => Math.max(0, u - 1));
    try { await markRead(id); } catch { /* keep optimistic */ }
  };
  const handleReadAll = async () => {
    setItems((p) => p.map((x) => ({ ...x, is_read: true })));
    setUnread(0);
    try { await markAllRead(); } catch { /* keep optimistic */ }
  };
  const handleDelete = async (id) => {
    setItems((p) => p.filter((x) => x.id !== id));
    try { await deleteNotification(id); } catch { /* refresh on next open */ }
  };

  const has = unread > 0;
  const BellIcon = has ? BellRinging : Bell;

  return (
    <>
      <button
        type="button"
        data-testid="notification-bell-button"
        onClick={() => setOpen(true)}
        aria-label={has ? `الإشعارات (${unread} غير مقروءة)` : "الإشعارات"}
        className="relative w-11 h-11 rounded-2xl flex items-center justify-center text-[#0A192F] hover:bg-white/40 active:scale-[0.94] transition-all duration-200"
      >
        <BellIcon
          size={26}
          weight={has ? "fill" : "duotone"}
          className={has ? "text-[#0A192F]" : "text-[#3F4750]"}
        />
        {has && (
          <>
            <span
              data-testid="notification-unread-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-extrabold text-[#0A192F] flex items-center justify-center ring-2 ring-white"
              style={{ background: "linear-gradient(135deg, #F4D464 0%, #D4AF37 60%, #A88425 100%)" }}
            >
              {unread > 99 ? "99+" : unread}
            </span>
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 w-[18px] h-[18px] rounded-full bg-[#D4AF37]/45 lexiq-bell-pulse pointer-events-none"
            />
          </>
        )}
      </button>

      {open && (
        <NotificationSheet
          items={items}
          unread={unread}
          loading={loading}
          onClose={() => setOpen(false)}
          onReadAll={handleReadAll}
          onRead={handleRead}
          onDelete={handleDelete}
        />
      )}

      <style>{`
        @keyframes lexiqBellPulse {
          0%, 100% { transform: scale(1); opacity: .55; }
          50%      { transform: scale(1.8); opacity: 0; }
        }
        .lexiq-bell-pulse { animation: lexiqBellPulse 2.2s ease-out infinite; }
      `}</style>
    </>
  );
}
